import React, { useEffect, useMemo } from 'react';
import Immutable, { List } from 'immutable';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Button,
  Image,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  Paragraph,
} from '@uncinc/react-drupal-core';

import { getVocabulary } from '../../../../store/actions/UI';
import { WysiwygField, Share } from '../../../../components';
import PrevNext from '../../../../components/PrevNext';
import StepsProgressBar from '../../../../components/StepsProgressBar';
import NewsletterBlock from '../../../../components/NewsletterBlock';
import MemberWall from '../../../../components/MemberWall';
import useLoggedInStatus from '../../../../hooks/useLoggedInstatus';
import './style.scss';

const useSteps = (vocabulary, campaign) => useMemo(() => {
  const paragraph = campaign.get('field_paragrafen', List()).find(
    (p) => p.getIn(['entity', 'type', 0, 'target_id'], '') === 'step_by_step_plan',
  );

  const steps = paragraph ? paragraph.getIn(['entity', 'field_subparagraphs'], List()).map((subparagraph) => {
    return {
      title: subparagraph.getIn(['entity', 'field_title', 0, 'value'], ''),
      url: subparagraph.getIn(['entity', 'field_link', 0, 'alias'], subparagraph.getIn(['entity', 'field_link', 0, 'uri'], '')),
      icon: subparagraph.getIn(['entity', 'field_media_image', 0, 'source']),
    };
  }).toJS() : [];


  const name = vocabulary.getIn(['name', 0, 'value'], '').toLowerCase();
  const currentStep = steps.findIndex((step) => step.title.toLowerCase() === name);
  const firstOrLast = currentStep === 0 || currentStep === steps.length - 1;

  return {
    steps,
    name,
    currentStep,
    firstOrLast,
  };
}, [vocabulary, campaign]);

const TaxonomyChainApproach = ({ entity }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedInStatus();
  const subTitle = entity.getIn(['field_sub_title', 0, 'value']);
  const tid = entity.getIn(['tid', 0, 'value']);
  const ctaTitle = entity.getIn(['field_cta_title', 0, 'value'], '');
  const ctaContent = entity.getIn(['field_cta_content', 0, 'processed'], '');
  const ctaLinkUri = entity.getIn(['field_cta_link', 0, 'alias'], entity.getIn(['field_cta_link', 0, 'uri']));
  const ctaLinkTitle = entity.getIn(['field_cta_link', 0, 'title'], t('general.read_more'));
  const campaignId = entity.getIn(['field_campaign', 0, 'target_id'], null);
  const campaignUrl = entity.getIn(['field_campaign', 0, 'url'], null);
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getVocabulary('campaign'));
  }, [dispatch]);

  const campaigns = useSelector((state) => state.getIn(['uiInternal', 'vocabularies', 'campaign'], Immutable.List()));
  const activeCampaign = campaigns.find((c) => c.getIn(['tid', 0, 'value']) === campaignId);
  const hasParagraphs = activeCampaign?.get('field_paragrafen')?.size > 0;

  const campaign = useMemo(() => {
    if (hasParagraphs) return activeCampaign;
    return entity.getIn(['field_campaign', 0, 'entity'], Immutable.Map());
  }, [hasParagraphs, activeCampaign, entity]);

  const { steps, currentStep, firstOrLast } = useSteps(entity, campaign);
  const icon = entity.getIn(['name', 0, 'value'], '').toLowerCase().replace(/ /g, '');

  return (
    <div className={`TaxonomyChainApproach ${firstOrLast ? 'TaxonomyChainApproach__firstLast' : ''}`}>
      <StepsProgressBar {...{ steps, currentStep }} noIndexFirstAndLast />
      <Container>
        <div className="row">
          <div className="col-12 col-lg-8 offset-lg-2">
            <div className="TaxonomyChainApproach__intro">

              <div className="TaxonomyChainApproach__intro__wrapper">
                <div className="TaxonomyChainApproach__intro__wrapper__header">
                  <div className="TaxonomyChainApproach__intro__wrapper__iconWrapper">
                    {steps[currentStep]?.icon ? (
                      <Image src={steps[currentStep].icon} alt={icon} />
                    ) : (
                      <i className={`icon icon-${icon} icon-variant-2`} />
                    )}
                  </div>
                  <h1>{entity.getIn(['name', 0, 'value'], '')}</h1>
                </div>
                <div className="TaxonomyChainApproach__intro__wrapper__subtitle">{subTitle}</div>
              </div>

              {entity.getIn(['description', 0, 'processed']) ? (
                <div className="TaxonomyChainApproach__body__wrapper">
                  <WysiwygField
                    property="schema:text"
                    className="TaxonomyChainApproach__body"
                    html={entity.getIn(['description', 0, 'processed'], '')}
                  />
                </div>
              ) : null}

            </div>
          </div>
        </div>
      </Container>

      <div className="NodeContentWrapper">
        {entity.get('field_paragrafen') ? (
          <div className="TaxonomyChainApproach__paragraphs">
            {entity.get('field_paragrafen', Immutable.List()).map((paragraph) => {
              const id = paragraph.get('target_id');
              const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
              return (
                <Paragraph
                  key={`paragraph:${id}:${language}`}
                  id={id}
                  entity={paragraph.get('entity')}
                />
              );
            })}
          </div>
        ) : null}

        <PrevNext
          originTid={tid}
          title={t('chainapproach.next_steps')}
          parentUrl={campaignUrl}
          parentUrlTitle={t('chainapproach.to_overview')}
          vocabulary="chainapproach"
          filterFunction={(item) => item.getIn(['field_campaign', 0, 'target_id']) === campaignId}
        />
        {ctaLinkUri && ctaLinkTitle && (
          <Container className="TaxonomyChainApproach__cta">
            <div className="row">
              <div className="col-12 col-lg-8 offset-lg-2">
                <h2>{ctaTitle}</h2>
                <WysiwygField
                  property="schema:text"
                  html={ctaContent}
                  className="subheading2"
                />
                <Button href={ctaLinkUri}>
                  {ctaLinkTitle} <i className="icon icon-arrow-right" />
                </Button>
              </div>
            </div>
          </Container>
        )}

        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Share />
            </div>
          </div>
        </Container>
      </div>

      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </div>
  );
};

export default TaxonomyChainApproach;
